<template>
  <div v-if="!data">
    <SkeletonLoader class="w-full h-40 mb-2 pt-24" />
    <SkeletonLoader class="w-full h-[78vh]" />
  </div>
  <div
    v-else
    ref="mapContainer"
    id="map-component"
    class="relative overflow-hidden"
    :class="[heightClass ?? 'h-[calc(100%-7rem)]', !fullScreen ? 'rounded-lg' : '']"
  >
    <!-- Changed from just relative -->
    <div
      class="absolute w-64 bg-white dark:bg-[#121212] shadow-md z-20 max-h-[calc(100%-1rem)] flex flex-col py-3 transform transition-transform duration-300 ease-in-out"
      :class="{
        'h-auto max-h-96': isMobile,
        'translate-y-[calc(100%-4rem)]': isMobile && !isExpanded,
        'top-12 my-28 mr-4 h-[calc(100%-11.5rem)] md:h-[calc(100%-17rem)] rounded-xl right-0': fullScreen,
        'my-2 md:h-[calc(100%-8rem)] rounded-md right-2': !fullScreen,
      }"
    >
      <div class="flex flex-col gap-2 justify-between px-4 cursor-pointer md:cursor-default">
        <span class="w-full flex items-center justify-between" @click="isExpanded = !isExpanded">
          <span class="text-xl">{{ $t('Attractions') }}</span>
          <svg
            class="w-6 h-6 md:hidden transform transition-transform"
            :class="{ 'rotate-180': !isExpanded }"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
        <!-- Toggle for show day trips start -->
        <span class="flex items-center gap-2">
          <label class="text-xs">
            {{ $t('Show Day Trips?') }}
          </label>
          <input type="checkbox" v-model="showDayTrips" class="form-checkbox h-3 w-3 text-tourscanner_blue-700" />
        </span>
        <!-- Toggle for show day trips end -->
      </div>
      <NuxtLinkLocale :to="`/s/${cityName}`" class="ts-link flex items-center justify-between px-4 py-2">{{
        data.city.name
      }}</NuxtLinkLocale>
      <div class="overflow-y-auto flex-1 pb-5">
        <ul>
          <li
            @click="zoomToLocation(destination)"
            v-for="destination in destinations"
            :key="destination.id"
            class="px-4 py-2 hover:bg-gray-100 hover:dark:bg-zinc-800 cursor-pointer text-black text-md leading-tight"
          >
            <StarRating :reviews_count="destination.reviews_count" :rating="destination.reviews_average ?? 0" />
            <NuxtLinkLocale v-if="isCrawler" :to="destination.url" class="ts-link text-sm">{{
              destination.label
            }}</NuxtLinkLocale>
            <a v-else="isCrawler" class="ts-link text-sm">{{ destination.label }}</a>
          </li>
        </ul>
      </div>
    </div>
    <!-- Legend Start-->
    <div
      class="absolute flex flex-col p-4 bg-white dark:bg-black rounded-lg shadow-lg transition-all duration-300 ease-in-out z-20 md:w-auto"
      :class="[
        showLegend
          ? 'w-4/5 max-h-4/5 overlfow-y-auto left-1/2 -translate-x-1/2 md:left-2 md:-translate-x-0'
          : '-translate-x-[calc(100%-6rem)] pl-12',
        fullScreen ? 'top-40' : 'top-16',
      ]"
    >
      <div class="flex gap-2 justify-between items-center w-full">
        <h3
          class="text-lg flex items-center gap-2 font-semibold text-black dark:text-white cursor-pointer"
          :class="{ 'text-sm': !showLegend }"
          @click="showLegend = !showLegend"
        >
          {{ $t('Legend') }}
          <img src="/img/icons/chevron-right.svg" class="w-4 h-4  dark:invert-1" v-show="!showLegend" />
        </h3>
        <img
          v-if="showLegend"
          src="/img/icons/close.svg"
          class="w-4 h-4  dark:invert-1"
          @click="showLegend = false"
        />
      </div>
      <ul
        class="w-full h-full flex flex-col overflow-auto md:grid md:grid-cols-2 md:overflow-hidden p-2"
        v-show="showLegend"
      >
        <li v-for="(label, index) in pinLabels" :key="index" class="flex items-center gap-2 text-xs">
          <img :src="`/img/icons/${getIconName(index + 1)}.svg`" alt="" class="w-4 h-4" />
          <span>{{ $t(label) }}</span>
        </li>
      </ul>
    </div>
    <!-- Legend End -->
    <div class="absolute">
      <div v-if="clickToLoad && !mapLoaded" class="map-container relative">
        <div class="absolute inset-0 bg-black opacity-50 z-20 md:z-10" v-if="!showMap"></div>
        <div
          v-if="!showMap"
          class="bg-white w-[90%] md:w-fit dark:bg-zinc-800 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 rounded-xl px-5 py-5 text-center shadow-md shadow-zinc-800"
        >
          <img src="/img/icons/ts_heart.svg" class="mx-auto mb-4 w-16 h-16" />
          <span class="text-3xl font-semibold block leading-tight mb-4">
            {{ $t('Explore all activities in {cityName}', { cityName: data.city.name }) }}
          </span>
          <button
            class="ts-button text-white px-6 py-3 rounded-lg transition-colors duration-200 font-medium"
            @click="loadInteractiveMap"
          >
            {{ $t('Show All Activities') }}
          </button>
        </div>
        <iframe
          :width="mapSize.width"
          :height="mapSize.height"
          style="border: 0"
          loading="lazy"
          :class="!showMap && isDark ? 'mapEmbedDark' : ''"
          allowfullscreen
          referrerpolicy="no-referrer-when-downgrade"
          :src="embedUrl"
        >
        </iframe>
      </div>
      <div
        ref="mapRef"
        class="map touch-none"
        :style="{ width: mapSize.width + 'px', height: mapSize.height + 'px' }"
        :class="!mapLoaded ? 'hidden' : ''"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { useNuxtApp } from '#app'; // Nuxt-specific import
import { createApp, onMounted, ref, watch } from 'vue';

import PinContent from '~/components/Map/PinComponent.vue';
import { handleApiResponse } from '~/server/utils/utils';

const pinLabels = [
  'Outdoor, Adventure & Sports',
  'Attractions',
  'Museums',
  'Food & Wine Experiences',
  'Shows, Music & Nightlife',
  'Theme Parks',
  'Day trips & Excursions',
  'Wellness',
  'Multi day trips',
  'Workshops & Classes',
  'Transfers & Transports',
  'Shopping & Fashion',
  'Shore Excursions',
  'Events: Concerts & Sport matches',
  'Sightseeing',
];

const showLegend = ref(false);

const { locale } = useI18n();

const currentLang = locale.value.split('-')[0];

const clickToLoad = true;
const mapLoaded = ref(false);
const waypoints = "Colosseum|Piazza Venezia|Trevi Fountain|Pantheon|Spanish Steps|St. Peter's Basilica|";
const showMap = ref(false);

const showDayTrips = ref(false);

const route = useRoute();
const destinations = ref([]);
const isExpanded = ref(false);
// const isMobile = ref(window?.innerWidth < 768);
const { isCrawler, isMobile } = useDevice();

const props = defineProps({
  cityName: {
    type: String,
    required: true,
  },
  heightClass: {
    type: String,
    required: true,
  },
  fullScreen: {
    type: Boolean,
    default: false,
  },
});
const { data, error } = await handleApiResponse(`/api/explore`, {
  params: {
    type: 'map',
    city_name: props.cityName,
    lang: currentLang,
  },
});
if (error.value) {
  console.error(error.value);
}

const embedUrl = ref(null);
if (data.value) {
  embedUrl.value = data.value.embedUrl;
  destinations.value = data.value.destinations;
}

const map = ref(null);
const mapRef = ref(null);
const mapContainer = ref(null);
const mapSize = ref({
  width: null,
  height: null,
});

function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    if (window.google && google.maps) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDD5gICfnmc3i9eBwIjjwapCq7U39qZXFs`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function fitMapToBounds() {
  if (!map.value || destinations.value.length === 0) return;
  const bounds = new google.maps.LatLngBounds();
  destinations.value.forEach((destination) => {
    bounds.extend({ lat: destination.latitude, lng: destination.longitude });
  });

  map.value.fitBounds(bounds);
}

function getIconName(destinationId) {
  let iconName;
  switch (destinationId) {
    case 1:
      iconName = 'pin_map_A';
      break;
    case 2:
      iconName = 'pin_map_B';
      break;
    case 3:
      iconName = 'pin_map_C';
      break;
    case 4:
      iconName = 'pin_map_D';
      break;
    case 5:
      iconName = 'pin_map_E';
      break;
    case 6:
      iconName = 'pin_map_F';
      break;
    case 7:
      iconName = 'pin_map_G';
      break;
    case 8:
      iconName = 'pin_map_H';
      break;
    case 9:
      iconName = 'pin_map_I';
      break;
    case 10:
      iconName = 'pin_map_J';
      break;
    case 11:
      iconName = 'pin_map_K';
      break;
    case 12:
      iconName = 'pin_map_L';
      break;
    case 13:
      iconName = 'pin_map_M';
      break;
    case 14:
      iconName = 'pin_map_N';
      break;
    case 15:
      iconName = 'pin_map_O';
      break;
    default:
      iconName = 'pin_map_A';
  }
  return iconName;
}

function createCustomMarker(destination) {
  const iconName = getIconName(destination.father_id);
  return new google.maps.Marker({
    position: { lat: destination.latitude, lng: destination.longitude },
    map: map.value,
    title: destination.label,
    icon: {
      url: `/img/icons/${iconName}.svg`,
      scaledSize: new google.maps.Size(30, 30),
    },
  });
}

function updateMarkers() {
  if (!map.value && !destinations.value) return;

  if (window.markers) {
    window.markers.forEach((marker) => marker.setMap(null));
  }
  window.markers = [];

  destinations.value.forEach((destination) => {
    if (destination.father_id == 7 && !showDayTrips.value) return;
    const marker = createCustomMarker(destination);

    marker.addListener('click', () => {
      zoomToLocation(destination);
    });

    window.markers.push(marker);
  });

  fitMapToBounds();
}
let previousInfoWindow = null;
async function zoomToLocation(destination) {
  if (!map.value) await loadInteractiveMap();
  if (map.value) {
    previousInfoWindow != null ? previousInfoWindow.close() : null;

    const marker = window.markers.find(
      (m) => m.getPosition().lat() === destination.latitude && m.getPosition().lng() === destination.longitude,
    );
    if (marker) {
      const infoWindow = new google.maps.InfoWindow({
        content: createPinContainer(destination),
      });
      infoWindow.open(map.value, marker);
      map.value.panTo(marker.getPosition());
      map.value.setZoom(15);
      previousInfoWindow = infoWindow;
    }
  }
}

function createPinContainer(destination) {
  // Create a container for the app instance
  const container = document.createElement('div');

  // Get Nuxt app instance
  const nuxtApp = useNuxtApp();

  // Mount the component with the current Nuxt context
  const appInstance = createApp({
    render() {
      return h(PinContent, { data: destination });
    },
  });

  // Ensure access to global properties
  appInstance.config.globalProperties = nuxtApp.vueApp.config.globalProperties;

  // If you need to add specific plugins (e.g., i18n, store), you should call them directly:
  if (nuxtApp.vueApp._context && nuxtApp.vueApp._context.plugins) {
    nuxtApp.vueApp._context.plugins.forEach((plugin) => {
      appInstance.use(plugin);
    });
  }

  // Mount the instance
  appInstance.mount(container);

  return container;
}

async function loadInteractiveMap() {
  if (isCrawler) {
    return;
  } else {
    await loadGoogleMapsScript();
    if (mapRef.value) {
      map.value = new google.maps.Map(mapRef.value, {
        center: { lat: 20, lng: 0 },
        zoom: 2,
        options: {
          gestureHandling: 'greedy',
        },
      });

      map.value.addListener('click', () => {
        if (previousInfoWindow) {
          previousInfoWindow.close();
          previousInfoWindow = null;
        }
      });

      updateMarkers();

      mapLoaded.value = true;
    }
  }
}

onMounted(() => {
  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      mapSize.value = {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      };
    }
  });

  if (mapContainer.value) {
    resizeObserver.observe(mapContainer.value);
  }

  onUnmounted(() => {
    resizeObserver.disconnect();
  });
});

watch(
  () => {
    destinations.value, showDayTrips.value;
  },
  () => {
    updateMarkers();
  },
  { deep: true },
);

const colorMode = useColorMode();
const isDark = computed({
  get() {
    return colorMode.value === 'dark';
  },
  set() {
    colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark';
  },
});
</script>

<style scoped>
.map {
  width: 100%;
  height: 100%;
  user-select: none;
}

.mapEmbedDark {
  filter: grayscale(100%) invert(92%) contrast(83%);
}
</style>
